export interface PasswordOptions {
    length: number;
    includeUppercase: boolean;
    includeLowercase: boolean;
    includeNumbers: boolean;
    includeSymbols: boolean;
    excludeAmbiguous: boolean;
}

export interface GeneratedPassword {
    password: string;
    strength: 'weak' | 'medium' | 'strong' | 'very-strong';
    strengthScore: number;
}

const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const NUMBERS = '0123456789';
const SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?';
const AMBIGUOUS = 'il1Lo0O';

function calculateStrength(password: string, options: PasswordOptions): { strength: GeneratedPassword['strength']; score: number } {
    let score = 0;
    
    // Length scoring
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    if (password.length >= 20) score += 1;
    
    // Character variety scoring
    if (options.includeLowercase && /[a-z]/.test(password)) score += 1;
    if (options.includeUppercase && /[A-Z]/.test(password)) score += 1;
    if (options.includeNumbers && /[0-9]/.test(password)) score += 1;
    if (options.includeSymbols && /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score += 1;
    
    let strength: GeneratedPassword['strength'];
    if (score <= 3) strength = 'weak';
    else if (score <= 5) strength = 'medium';
    else if (score <= 7) strength = 'strong';
    else strength = 'very-strong';
    
    return { strength, score };
}

export function generatePassword(options: PasswordOptions): GeneratedPassword {
    let charset = '';
    
    if (options.includeLowercase) charset += LOWERCASE;
    if (options.includeUppercase) charset += UPPERCASE;
    if (options.includeNumbers) charset += NUMBERS;
    if (options.includeSymbols) charset += SYMBOLS;
    
    // Remove ambiguous characters if requested
    if (options.excludeAmbiguous) {
        charset = charset.split('').filter(c => !AMBIGUOUS.includes(c)).join('');
    }
    
    // Fallback to lowercase if nothing selected
    if (charset.length === 0) {
        charset = LOWERCASE;
    }
    
    let password = '';
    const array = new Uint32Array(options.length);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < options.length; i++) {
        password += charset[array[i] % charset.length];
    }
    
    const { strength, score } = calculateStrength(password, options);
    
    return {
        password,
        strength,
        strengthScore: score,
    };
}

export function generatePasswords(options: PasswordOptions, count: number): GeneratedPassword[] {
    const passwords: GeneratedPassword[] = [];
    for (let i = 0; i < count; i++) {
        passwords.push(generatePassword(options));
    }
    return passwords;
}

export const DEFAULT_PASSWORD_OPTIONS: PasswordOptions = {
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeAmbiguous: false,
};
